/**
 * Viewport-width hook for the calendar's responsive layout.
 *
 * Backed by matchMedia rather than a resize listener so it fires once per
 * threshold crossing instead of once per resize frame — and so a phone
 * rotating between portrait and landscape is a single state change.
 */

import { useState, useEffect } from 'react';

/** Below this the left panel overlays the calendar instead of sitting beside it. */
export const NARROW_VIEWPORT_PX = 1024;

export function useIsNarrowViewport(breakpoint: number = NARROW_VIEWPORT_PX): boolean {
  const query = `(max-width: ${breakpoint - 1}px)`;

  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    setIsNarrow(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [query]);

  return isNarrow;
}
